import { useState, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, FileText, Check, Clock, AlertCircle, Archive } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { getFileIconByName } from '../FileIcons';
import type { CaseDocument, DocumentStatus } from '../../types';
import { DOCUMENT_STATUS_CONFIG } from '../../types';
import { cn } from '../ui/utils';

interface DocumentsPanelProps {
  documents: CaseDocument[];
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

type StatusFilter = 'all' | DocumentStatus;

interface DocumentTreeNodeProps {
  document: CaseDocument;
  depth: number;
  selectedDocumentId: string | null;
  onSelectDocument: (documentId: string) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (documentId: string) => void;
  formatDate: (dateString: string) => string;
  formatFileSize: (bytes: number) => string;
  getStatusIcon: (status: DocumentStatus) => React.ReactNode;
}

/**
 * Recursive tree node component for displaying documents with children.
 */
function DocumentTreeNode({
  document,
  depth,
  selectedDocumentId,
  onSelectDocument,
  expandedNodes,
  onToggleExpand,
  formatDate,
  formatFileSize,
  getStatusIcon,
}: DocumentTreeNodeProps) {
  const hasChildren = document.children && document.children.length > 0;
  const isExpanded = expandedNodes.has(document.id);
  const isSelected = selectedDocumentId === document.id;
  const isContainer = document.is_container;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(document.id);
  };

  return (
    <div>
      <button
        onClick={() => onSelectDocument(document.id)}
        className={cn(
          'w-full text-left py-2.5 hover:bg-gray-50 transition-colors border-l-2',
          isSelected
            ? 'bg-[#f9f5ff] border-l-[#7035C4]'
            : 'border-l-transparent'
        )}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
      >
        <div className="flex items-start gap-2">
          {/* Expand/collapse chevron */}
          {hasChildren ? (
            <button
              onClick={handleChevronClick}
              className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded transition-colors mt-0.5"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-gray-500 transition-transform',
                  !isExpanded && '-rotate-90'
                )}
              />
            </button>
          ) : (
            <div className="w-4.5 flex-shrink-0" /> // Spacer for alignment
          )}

          {/* Document icon */}
          <div className="flex-shrink-0 mt-0.5">
            {isContainer ? (
              <Archive className="h-4 w-4 text-amber-600" />
            ) : (
              getFileIconByName(document.name)
            )}
          </div>

          {/* Document info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" title={document.name}>
              {document.name}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span>{formatDate(document.upload_date)}</span>
              <span>•</span>
              <span>{formatFileSize(document.file_size)}</span>
              {document.page_count && !isContainer && (
                <>
                  <span>•</span>
                  <span>{document.page_count} pg</span>
                </>
              )}
              {hasChildren && (
                <>
                  <span>•</span>
                  <span>{document.children!.length} items</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              {getStatusIcon(document.status)}
              <span
                className="text-xs"
                style={{ color: DOCUMENT_STATUS_CONFIG[document.status].color }}
              >
                {DOCUMENT_STATUS_CONFIG[document.status].label}
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Recursively render children */}
      {hasChildren && isExpanded && (
        <div>
          {document.children!.map((child) => (
            <DocumentTreeNode
              key={child.id}
              document={child}
              depth={depth + 1}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={onSelectDocument}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              formatDate={formatDate}
              formatFileSize={formatFileSize}
              getStatusIcon={getStatusIcon}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build a tree structure from flat documents list using parent_id.
 * Returns only root documents (those without a parent_id).
 */
function buildDocumentTree(documents: CaseDocument[]): CaseDocument[] {
  // Create a map of document ID to document (with empty children array)
  const docMap = new Map<string, CaseDocument>();
  documents.forEach((doc) => {
    docMap.set(doc.id, { ...doc, children: [] });
  });

  // Build tree by assigning children to parents
  const rootDocs: CaseDocument[] = [];
  documents.forEach((doc) => {
    const docWithChildren = docMap.get(doc.id)!;
    if (doc.parent_id && docMap.has(doc.parent_id)) {
      const parent = docMap.get(doc.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(docWithChildren);
    } else {
      // No parent or parent not in list - treat as root
      rootDocs.push(docWithChildren);
    }
  });

  return rootDocs;
}

/**
 * Filter document tree, keeping nodes that match filter or have matching descendants.
 * Also keeps ancestors of matching nodes.
 */
function filterDocumentTree(
  documents: CaseDocument[],
  statusFilter: StatusFilter
): CaseDocument[] {
  if (statusFilter === 'all') return documents;

  function filterNode(doc: CaseDocument): CaseDocument | null {
    // Filter children recursively
    const filteredChildren = (doc.children || [])
      .map(filterNode)
      .filter((c): c is CaseDocument => c !== null);

    // Include this node if it matches OR has matching descendants
    if (doc.status === statusFilter || filteredChildren.length > 0) {
      return { ...doc, children: filteredChildren };
    }

    return null;
  }

  return documents.map(filterNode).filter((d): d is CaseDocument => d !== null);
}

/**
 * Count total documents in tree (including nested children).
 */
function countDocuments(documents: CaseDocument[]): number {
  let count = 0;
  function countNode(doc: CaseDocument) {
    count++;
    (doc.children || []).forEach(countNode);
  }
  documents.forEach(countNode);
  return count;
}

export function DocumentsPanel({
  documents,
  selectedDocumentId,
  onSelectDocument,
  isCollapsed,
  onToggleCollapse,
}: DocumentsPanelProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => {
    // Start with all containers expanded
    const expanded = new Set<string>();
    documents.forEach((doc) => {
      if (doc.is_container || (doc.children_count && doc.children_count > 0)) {
        expanded.add(doc.id);
      }
    });
    return expanded;
  });

  // Build tree structure from flat documents
  const documentTree = useMemo(() => buildDocumentTree(documents), [documents]);

  // Apply status filter to tree
  const filteredTree = useMemo(
    () => filterDocumentTree(documentTree, statusFilter),
    [documentTree, statusFilter]
  );

  const totalCount = useMemo(() => countDocuments(documentTree), [documentTree]);

  const onToggleExpand = useCallback((documentId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'completed':
        return <Check className="h-3.5 w-3.5 text-green-500" />;
      case 'redacted':
        return <Check className="h-3.5 w-3.5 text-blue-500" />;
      case 'needs_redaction':
        return <AlertCircle className="h-3.5 w-3.5 text-amber-500" />;
      case 'unprocessed':
      default:
        return <Clock className="h-3.5 w-3.5 text-gray-400" />;
    }
  };

  // Collapsed state - just show toggle button
  if (isCollapsed) {
    return (
      <div className="w-10 border-r border-border bg-gray-50 flex flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          aria-label="Expand documents panel"
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>
        <div className="mt-4 flex flex-col items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          <span className="text-xs text-gray-500 writing-vertical-lr rotate-180">
            {totalCount} docs
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-border bg-white flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-sm">Documents</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {totalCount}
          </span>
        </div>
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          aria-label="Collapse documents panel"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {/* Filter */}
      <div className="p-3 border-b border-border">
        <Select
          value={statusFilter}
          onValueChange={(value: string) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {(Object.keys(DOCUMENT_STATUS_CONFIG) as DocumentStatus[]).map((status) => (
              <SelectItem key={status} value={status}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: DOCUMENT_STATUS_CONFIG[status].color }}
                  />
                  {DOCUMENT_STATUS_CONFIG[status].label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Document Tree */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredTree.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {totalCount === 0 ? 'No documents yet' : 'No documents match filter'}
          </div>
        ) : (
          <div className="py-1">
            {filteredTree.map((doc) => (
              <DocumentTreeNode
                key={doc.id}
                document={doc}
                depth={0}
                selectedDocumentId={selectedDocumentId}
                onSelectDocument={onSelectDocument}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
                getStatusIcon={getStatusIcon}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
